import React from 'react';
import styled from 'styled-components';
import { ProjectInfoData } from '../../../shared/dto/EntityRepository';
import { useProjectInfoActions } from '../../../shared/hooks/useApi/useProjectInfo';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { ModalType, ProjectInfoType } from '../../../shared/enum/EnumRepository';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import { PIInput } from '../../../shared/component/headless/input/InputBody';
import deleteCloseIcon from '../../../asset/icon/delete_close.png';
import deleteOpenIcon from '../../../asset/icon/delete_open.png';
import orangeIcon from '../../../asset/icon/orange.png';
import { imHere } from '../../../shared/component/Animation';
import { getHintMessage } from '../../../shared/constants/hintMessages';
import { getProjectInfoNamePlaceholder } from '../../../shared/util/getProjectInfoTypeLabel';
import ProjectInfoValues from '../ProjectInfoValues';
import { getProjectInfoDisplayName, normalizeProjectInfoType } from '../../../shared/util/projectInfo';

interface ProjectInfoProps {
  data: ProjectInfoData;
}

const ProjectInfoMobile: React.FC<ProjectInfoProps> = ({ data }) => {
  const { isEditMode } = useEditMode();
  const setStandardModal = useModalStore((s) => s.setStandardModal);
  const { deleteProjectInfo } = useProjectInfoActions();
  const projectInfoType = normalizeProjectInfoType(data.type);
  const visibleValues = projectInfoType === ProjectInfoType.ACTIVE_DATE
    ? data.customValues.slice(0, 1)
    : data.customValues;
  const displayName = getProjectInfoDisplayName(data.customName);
  const editHint = getHintMessage('EDIT_INFO');
  const deleteHint = getHintMessage('DELETE_INFO');

  const handleEdit = () => {
    setStandardModal({
      modalType: ModalType.EDIT_PROJECT_INFO_MOBILE,
      title: editHint,
      value: data.id,
      handleChange: () => {},
    });
  };

  const handleDelete = async () => {
    try {
      await deleteProjectInfo({ id: data.id });
    } catch (err) {
    }
  }

  return (
    <ProjectInfoItem
      data-has-changed={data.hasChanged ? "true" : "false"}    // 편집 모드 진입 시 변경사항이 있으면 스크롤
    >
      {isEditMode ? (
        <>
          <EditButton type="button" onClick={handleEdit} aria-label={`${editHint}: ${displayName || getProjectInfoNamePlaceholder(projectInfoType)}`}>
            <InputPreview>{displayName || getProjectInfoNamePlaceholder(projectInfoType)}</InputPreview>
            <ValueList>
              {visibleValues.length > 0
                ? visibleValues.map((value, index) => <InputPreview key={index}>{value.text || '—'}</InputPreview>)
                : <InputPreview>—</InputPreview>}
            </ValueList>
          </EditButton>
          <DeleteButtonWrapper>
            <UniversalIconBtn
              size="fit"
              border={false}
              backgroundImg={deleteCloseIcon}
              backgroundImgHover={deleteOpenIcon}
              onClick={handleDelete}
              aria-label={deleteHint}
            />
            {data.hasChanged && (
              <ChangedIconWrap>
                <ChangedIcon src={orangeIcon} alt="Unsaved changes" />
              </ChangedIconWrap>
            )}
          </DeleteButtonWrapper>
        </>
      ) : (
        <ProjectInfoContentComp>
          <LabelWrapper><NameSection>{displayName}</NameSection></LabelWrapper>
          <ProjectInfoValues data={data} onDelete={handleDelete} />
        </ProjectInfoContentComp>
      )}
    </ProjectInfoItem>
  );
}

const ProjectInfoItem = styled.div`
  position: relative;
  display: flex;
  height: fit-content;
  gap: clamp(12px, 1vw, 14px);
  margin-bottom: clamp(6px, 1.8vw, 2rem);
`;

const ProjectInfoContentComp = styled.div`
  display: flex;
  align-items: flex-start;
  gap: clamp(12px, 4vw, 56px);
  flex: 1;
  min-width: 0;
`;

const LabelWrapper = styled.div`
  width: clamp(70px, 12vw, 150px);
  display: flex;
`;

const NameSection = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 0px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_03_2};
  min-width: 0;
  overflow-wrap: anywhere;
`;

const EditButton = styled.button`
  display: grid;
  grid-template-columns: clamp(70px, 22vw, 100px) minmax(0, 1fr);
  align-items: start;
  gap: 12px;
  flex: 1;
  min-width: 0;
  min-height: 36px;
  padding: 0;
  background: transparent;
  text-align: left;
`;

const ValueList = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 4px;
`;

const InputPreview = styled(PIInput).attrs({ as: 'span' })`
  display: block;
  min-width: 0;
  margin-top: 0;
  padding: 5px 0;
  overflow-wrap: anywhere;
  cursor: pointer;
`;

const DeleteButtonWrapper = styled.div`
  position: relative;
  align-self: flex-start;
  flex-shrink: 0;
  padding-top: 4px;
`;

const ChangedIconWrap = styled.div`
  position: absolute;
  left: 100%;
  top: 4px;
  height: 26px;
  display: flex;
  align-items: center;
  ${imHere}
`;

const ChangedIcon = styled.img`
  width: 12px;
  height: 12px;
  margin: 4px;
`;

export default React.memo(ProjectInfoMobile);
